import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES, buildCampaignWizardPath } from "../../utils/campaignWizardNavigation";
import { getNicheByPath, isNicheId } from "../../constants/niches";
import { setActiveNiche } from "../../store/nicheSlice";
import bannerImage from "../../assets/bannerimage.jpeg";
import { trackCtaClicked, trackFeatureStarted } from "../../analytics/analytics";
import {
  ANALYTICS_FEATURES,
  ANALYTICS_CTAS,
  ANALYTICS_ENTRY_POINTS,
} from "../../analytics/analytics.constants";
import "bootstrap-icons/font/bootstrap-icons.css";

const CAROUSEL_INTERVAL_MS = 5000;
const SWIPE_THRESHOLD_PX = 50;

/** Fixed banner height so carousel slides don't jump. */
const BANNER_HEIGHT_CLASS = "h-[19.5rem] sm:h-[16.5rem] lg:h-[17.5rem]";

function BannerCtaButton({ onCtaClick, ctaLabel, showSocialIcons, ctaIconClass }) {
  return (
    <button
      type="button"
      onClick={onCtaClick}
      className="btn-primary-app btn-animated-shine inline-flex w-auto shrink-0 items-center justify-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold shadow-md shadow-slate-900/25 sm:gap-2 sm:px-5 sm:py-2.5 sm:text-sm sm:shadow-lg"
    >
      {ctaIconClass ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-white/15 text-white shadow-sm sm:h-8 sm:w-8">
          <i className={`bi ${ctaIconClass} text-[10px] leading-none sm:text-sm`} />
        </span>
      ) : showSocialIcons ? (
        <>
          <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-black text-white shadow-sm sm:h-8 sm:w-8">
            <i className="bi bi-facebook text-[10px] leading-none sm:text-sm" />
          </span>
          <span className="-ml-2.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-white text-black shadow-sm ring-1 ring-black/10 sm:-ml-3 sm:h-8 sm:w-8">
            <i className="bi bi-instagram text-[10px] leading-none sm:text-sm" />
          </span>
        </>
      ) : null}
      <span>{ctaLabel}</span>
      <span aria-hidden className="text-sm leading-none sm:text-base">
        &rarr;
      </span>
    </button>
  );
}

function BannerSlide({
  title,
  subtitle = null,
  description,
  backgroundImage,
  onCtaClick,
  ctaLabel,
  showSocialIcons,
  ctaIconClass,
}) {
  const hasNicheBackground = Boolean(backgroundImage);

  return (
    <div
      className={`surface-card relative flex ${BANNER_HEIGHT_CLASS} flex-shrink-0 basis-full flex-col overflow-hidden max-sm:rounded-none max-sm:rounded-b-[2rem] max-sm:border-x-0 max-sm:border-t-0 max-sm:shadow-[0_10px_28px_-14px_rgba(15,23,42,0.18)] ${
        hasNicheBackground ? "bg-transparent" : "max-lg:bg-transparent lg:bg-white"
      }`}
    >
      {hasNicheBackground ? (
        <>
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat lg:bg-right"
            style={{ backgroundImage: `url(${backgroundImage})` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/95 via-white/80 to-white/15 lg:bg-gradient-to-r lg:from-white/97 lg:via-white/94 lg:to-white/78"
            aria-hidden
          />
        </>
      ) : (
        <>
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center lg:hidden"
            style={{ backgroundImage: `url(${bannerImage})` }}
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 bg-white/90 lg:hidden" aria-hidden />
          <div
            className="pointer-events-none absolute inset-0 hidden opacity-[0.45] lg:block"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #cbd5e1 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 left-0 hidden w-1 bg-gradient-to-b from-amber-400 via-emerald-400 to-sky-400 lg:block"
            aria-hidden
          />
        </>
      )}

      <div className="relative flex h-full min-h-0 flex-col gap-3 px-4 pb-5 pt-5 sm:p-6 lg:px-8 lg:py-8">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:max-w-[60%]">
          <h2 className="shrink-0 text-lg font-semibold tracking-tight text-[#021645] sm:text-xl lg:text-2xl">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1.5 shrink-0 text-sm font-medium leading-snug text-slate-800 lg:mt-2 lg:text-[0.95rem]">
              {subtitle}
            </p>
          ) : null}
          <p className="mt-2 line-clamp-3 min-h-0 flex-1 text-sm leading-relaxed text-slate-600 lg:mt-3 lg:line-clamp-3 lg:text-base">
            {description}
          </p>

          <div className="mt-auto self-start pt-3">
            <BannerCtaButton
              onCtaClick={onCtaClick}
              ctaLabel={ctaLabel}
              showSocialIcons={showSocialIcons}
              ctaIconClass={ctaIconClass}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GuestHomeBanner({
  title = "Need something to post today?",
  subtitle = null,
  description = "Use the post builder to choose trends, generate post ideas, and get ready to publish.",
  wizardRef = APP_ROUTES.HOME,
  isDashboard = false,
  backgroundImage = null,
  /** @type {Array<{ id: string, title: string, subtitle?: string, description: string, backgroundImage: string, wizardRef?: string }> | null} */
  slides = null,
  onCtaClick = null,
  ctaLabel = "Generate Your First Post",
  ctaAnalyticsId = ANALYTICS_CTAS.CREATE_FIRST_POST,
  showSocialIcons = true,
  ctaIconClass = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragDelta, setDragDelta] = useState(0);
  const touchStartX = useRef(null);

  const isCarousel = Array.isArray(slides) && slides.length > 0;
  const slideCount = isCarousel ? slides.length : 0;

  useEffect(() => {
    if (!isCarousel || slideCount < 2 || paused) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slideCount);
    }, CAROUSEL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isCarousel, slideCount, paused]);

  const goToSlide = (index) => {
    if (slideCount === 0) return;
    setActiveIndex(((index % slideCount) + slideCount) % slideCount);
  };

  const resolveNicheId = (nicheId, slideWizardRef) => {
    if (isNicheId(nicheId)) return nicheId;
    return getNicheByPath(slideWizardRef)?.id ?? null;
  };

  const handleCtaClick = (slideWizardRef, nicheId = null) => {
    trackCtaClicked(ctaAnalyticsId);
    if (isDashboard) {
      trackFeatureStarted(ANALYTICS_FEATURES.CAMPAIGN_BUILDER, {
        entry_point: ANALYTICS_ENTRY_POINTS.DASHBOARD_BANNER,
      });
    }

    const resolvedNicheId = resolveNicheId(nicheId, slideWizardRef ?? wizardRef);
    if (resolvedNicheId) {
      dispatch(setActiveNiche(resolvedNicheId));
    }

    if (onCtaClick) {
      onCtaClick();
      return;
    }
    navigate(buildCampaignWizardPath(APP_ROUTES.HOME, null, { fastForward: true }));
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    setPaused(true);
  };

  const handleTouchMove = (event) => {
    if (touchStartX.current == null) return;
    setDragDelta(event.touches[0].clientX - touchStartX.current);
  };

  const handleTouchEnd = () => {
    if (touchStartX.current == null) return;
    if (dragDelta <= -SWIPE_THRESHOLD_PX) {
      goToSlide(activeIndex + 1);
    } else if (dragDelta >= SWIPE_THRESHOLD_PX) {
      goToSlide(activeIndex - 1);
    }
    touchStartX.current = null;
    setDragDelta(0);
    setPaused(false);
  };

  if (!isCarousel) {
    return (
      <section
        ref={bannerRef}
        aria-labelledby="guest-home-banner-title"
        className="mb-6 max-sm:-mx-5 max-sm:-mt-12 sm:mx-0 sm:mt-0"
      >
        <BannerSlide
          title={title}
          subtitle={subtitle}
          description={description}
          backgroundImage={backgroundImage}
          onCtaClick={() => handleCtaClick(wizardRef)}
          ctaLabel={ctaLabel}
          showSocialIcons={showSocialIcons}
          ctaIconClass={ctaIconClass}
        />
      </section>
    );
  }

  return (
    <section
      ref={bannerRef}
      aria-roledescription="carousel"
      aria-label="Explore niches"
      className="mb-6 max-sm:-mx-5 max-sm:-mt-12 sm:mx-0 sm:mt-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className={`overflow-hidden max-sm:rounded-b-[2rem] ${BANNER_HEIGHT_CLASS}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`flex h-full ${dragDelta === 0 ? "transition-transform duration-500 ease-out" : ""}`}
          style={{
            transform: `translateX(calc(-${activeIndex * 100}% + ${dragDelta}px))`,
          }}
        >
          {slides.map((slide) => (
            <BannerSlide
              key={slide.id}
              title={slide.title}
              subtitle={slide.subtitle}
              description={slide.description}
              backgroundImage={slide.backgroundImage}
              onCtaClick={() => handleCtaClick(slide.wizardRef, slide.id)}
              ctaLabel={ctaLabel}
              showSocialIcons={showSocialIcons}
              ctaIconClass={ctaIconClass}
            />
          ))}
        </div>
      </div>

      {slideCount > 1 ? (
        <div
          className="mt-3 flex items-center justify-center gap-2"
          role="tablist"
          aria-label="Banner slides"
        >
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`Show ${slide.title}`}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === activeIndex
                  ? "w-6 bg-slate-900"
                  : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
